/**
 * React-free transforms behind the billing tab's "current plan" block.
 *
 * The whole point of this file is one fact that the old per-site card ignored: billing is
 * an ACCOUNT-level concern. `GET /api/account/billing` returns one row per managed site,
 * but `resolveEntitlements(siteId)` delegates straight to `resolveAccountEntitlements`, so
 * planCode, status, quota, billingCycle, features and plan are identical for every sibling
 * site. The only genuinely per-site field is `siteName`. Rendering the full card once per
 * site therefore repeated the same 37 entitlement rows N times.
 */
import { UNLIMITED, type BillingPlan, type BillingSite, type EntitlementDef, type EntitlementMap } from "../../../lib/billing";

/** One billing account and every site its single subscription covers. */
export interface AccountPlanGroup {
  /** React key. The account id, or `site:<id>` for a site that has no account yet. */
  key: string;
  accountId: string | null;
  /** Every site the group's subscription covers, in server order. */
  sites: BillingSite[];
  /** The row the plan facts are read from. Every site in the group carries the same values. */
  primary: BillingSite;
  /**
   * Whether this group owns the subscription controls (renewal date, cancel/resume, refund,
   * add-ons, the quota site picker).
   *
   * Those all come from a SINGLE `X-Site-Id` request built from `data.sites[0]`, so they
   * describe exactly one account. Before grouping, N identical cards hid that; after
   * grouping, a user who manages sites in two accounts would otherwise see account A's
   * renewal date and cancel button sitting on account B's card. Only the group that
   * actually contains `billingSiteId` may render them.
   */
  ownsSubscriptionControls: boolean;
}

/**
 * Collapse the per-site rows into one group per billing account.
 *
 * Sites without an `accountId` cannot share a subscription with anything, so each becomes
 * its own group (keyed `site:<id>`) rather than being merged into a null bucket. Group
 * order follows the server's site order, which keeps the account the user most recently
 * looked at from jumping around between loads.
 */
export function groupSitesByAccount(sites: BillingSite[], billingSiteId: string | null): AccountPlanGroup[] {
  const groups = new Map<string, AccountPlanGroup>();
  for (const site of sites) {
    const key = site.accountId ?? `site:${site.siteId}`;
    const existing = groups.get(key);
    if (existing) {
      existing.sites.push(site);
      continue;
    }
    groups.set(key, {
      key,
      accountId: site.accountId,
      sites: [site],
      primary: site,
      ownsSubscriptionControls: false,
    });
  }
  const list = [...groups.values()];
  // The subscription controls belong to the group holding the site whose id every
  // `X-Site-Id` call is made with. With a single account (the common case) that is the
  // only group, so nothing changes for almost everyone.
  const owner = billingSiteId ? list.find((g) => g.sites.some((s) => s.siteId === billingSiteId)) : list[0];
  if (owner) owner.ownsSubscriptionControls = true;
  return list;
}

/**
 * The price actually being paid, for the cycle actually in effect.
 *
 * The old card always read `priceMonthlyCents` and always appended "kuus", so a yearly
 * subscriber was shown a number they do not pay next to a period they do not pay it on.
 */
export function planPriceCents(plan: BillingPlan | null, cycle: "monthly" | "yearly"): number {
  if (!plan) return 0;
  return cycle === "yearly" ? plan.priceYearlyCents : plan.priceMonthlyCents;
}

/** Does the plan actually grant this entitlement? Mirrors the comparison table's cells:
 *  a boolean must be true, and a limit/quota of 0 means "not offered" rather than "zero". */
export function isGranted(def: EntitlementDef, features: EntitlementMap): boolean {
  const value = features[def.key];
  if (def.type === "boolean") return value === true;
  if (value === UNLIMITED) return true;
  return typeof value === "number" && value > 0;
}

/**
 * How many catalog entries the plan grants and how many it does not.
 *
 * Both numbers are shown on the disclosure buttons. The count is what stops a collapsed
 * card from reading as though data went missing: "Paketis sisaldub (23)" states the size of
 * what is hidden, so the click is an informed one.
 */
export function countEntitlements(
  catalog: EntitlementDef[],
  features: EntitlementMap,
): { granted: number; missing: number } {
  let granted = 0;
  for (const def of catalog) if (isGranted(def, features)) granted += 1;
  return { granted, missing: catalog.length - granted };
}
